import { ruleIdFromSyntheticId, syntheticGiftId } from '../gift-id'
import type { CartLine } from './cart-math'

/**
 * "Buy three pizzas, get a garlic bread."
 *
 * A gift the builder offers but never adds is a promotion the customer was
 * promised and did not get; one it adds twice is stock given away. Both are
 * counted here rather than in the JSX, and both are tested.
 *
 * Ported from legacy `pages/new-order.tsx`.
 */

/** Nobody has ever meant to give away more than this in one press. */
const MAX_GIFTS_PER_OFFER = 20

export interface ProductGiftRule {
  id: number
  active?: boolean
  /** How many matching items earn one gift. */
  minCount?: number
  /** Matched against a cart line's category name, case-insensitively. */
  categoryKeyword?: string
  giftName?: string
  giftPrice?: number | string
  /** Whether six matching items earn two gifts, or still only one. */
  multiply?: boolean
}

export interface GiftOffer {
  ruleId: number
  ruleType: 'product'
  giftName: string
  giftPrice: number
  /** How many are still owed, after what is already in the cart. */
  count: number
}

export { ruleIdFromSyntheticId, syntheticGiftId }

/** How many gifts from this rule the cart already holds. */
export function giftsInCartFor(cart: CartLine[], ruleId: number): number {
  return cart
    .filter((line) => line.productId < 0)
    .reduce(
      (count, line) =>
        ruleIdFromSyntheticId(line.productId) === ruleId ? count + line.quantity : count,
      0,
    )
}

/**
 * How many items in the cart count towards a rule.
 *
 * Only real products: gifts have negative ids, and a gift must not earn another
 * gift. Custom pizzas and lunch menus also use non-positive ids and are
 * likewise excluded — as legacy does.
 */
export function matchingQuantity(cart: CartLine[], keyword: string): number {
  const needle = keyword.trim().toLowerCase()
  if (!needle) return 0
  return cart
    .filter((line) => line.productId > 0)
    .reduce(
      (total, line) =>
        (line.categoryName ?? '').toLowerCase().includes(needle) ? total + line.quantity : total,
      0,
    )
}

/** A rule the cart could earn but has not been given yet. */
export function pendingGiftOffers(cart: CartLine[], rules: ProductGiftRule[]): GiftOffer[] {
  const offers: GiftOffer[] = []

  for (const rule of rules) {
    if (!rule.active) continue

    const minCount = rule.minCount
    if (!Number.isInteger(minCount) || (minCount ?? 0) < 1) continue

    const giftPrice = Number(rule.giftPrice)
    if (!Number.isFinite(giftPrice) || giftPrice < 0) continue

    const giftName = rule.giftName?.trim()
    if (!giftName) continue

    const keyword = rule.categoryKeyword?.trim().toLowerCase() ?? ''
    if (!keyword) continue

    const matched = matchingQuantity(cart, keyword)
    const earned = rule.multiply
      ? Math.floor(matched / (minCount ?? 1))
      : matched >= (minCount ?? 1)
        ? 1
        : 0

    const owed = Math.max(0, earned - giftsInCartFor(cart, rule.id))
    if (owed > 0) {
      offers.push({ ruleId: rule.id, ruleType: 'product', giftName, giftPrice, count: owed })
    }
  }

  return offers
}

/** The cart lines that accepting an offer adds. */
export function giftLinesFor(offer: GiftOffer, cart: CartLine[]): CartLine[] {
  const alreadyGiven = giftsInCartFor(cart, offer.ruleId)
  const toAdd = Math.min(offer.count, MAX_GIFTS_PER_OFFER)

  return Array.from({ length: toAdd }, (_unused, index) => ({
    productId: syntheticGiftId(offer.ruleId, alreadyGiven + index + 1),
    productName: offer.giftName,
    unitPrice: offer.giftPrice,
    quantity: 1,
    giftRuleId: offer.ruleId,
    giftRuleType: offer.ruleType,
  }))
}
